import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Spin, message, Row, Col, Statistic, Select, Button, Tabs, Checkbox } from 'antd';
import {
  ThunderboltOutlined,
  DatabaseOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CalendarOutlined,
  BarChartOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import dayjs from 'dayjs';
import { makePostCall } from '../../utils/helper';
import {
  normalizeBodyPart,
  DEFAULT_BODY_PARTS_BY_MODALITY,
  BODY_PART_PRIORITY_ORDER,
  getPerformanceBenchmark,
  getServiceLineSlope
} from '../../utils/bodyPartNormalization';
import './styles.css';

interface CenterData {
  center_id: string;
  center_name?: string;
  total_users: number;
  active_days: number;
  total_studies_all_modalities: number;
  total_views: number;
  ct_studies: number | null;
  ct_avg_seconds: number | null;
  ct_p90_seconds: number | null;
  mr_studies: number | null;
  mr_avg_seconds: number | null;
  mr_p90_seconds: number | null;
  dx_studies: number | null;
  dx_avg_seconds: number | null;
  dx_p90_seconds: number | null;
  total_pushed: number | null;
  ct_pushed: number | null;
  mr_pushed: number | null;
  dx_pushed: number | null;
  performance_status: 'new' | 'fast' | 'medium' | 'slow';
}

interface SummaryData {
  total_centers: number;
  total_studies: number;
  total_ct_studies: number;
  total_mr_studies: number;
  total_dx_studies: number;
  total_users: number;
  total_views: number;
  total_pushed: number;
  total_ct_pushed: number;
  total_mr_pushed: number;
  total_dx_pushed: number;
}

const CenterAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CenterData[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [overviewSortedInfo, setOverviewSortedInfo] = useState<any>({});
  const [performanceSortedInfo, setPerformanceSortedInfo] = useState<any>({});
  const [selectedCenters, setSelectedCenters] = useState<string[]>([]);

  // Date filter state - default to "last_month"
  const [dateFilter, setDateFilter] = useState<string>('yesterday');

  // 7-day trend state
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendData, setTrendData] = useState<any>(null);
  const [selectedTrendCenter, setSelectedTrendCenter] = useState<string>('all_centers');

  // Scatter plot state
  const [scatterLoading, setScatterLoading] = useState(false);
  const [scatterData, setScatterData] = useState<any>(null);
  const [selectedModality, setSelectedModality] = useState<string>('CT');
  const [selectedBodyParts, setSelectedBodyParts] = useState<string[]>([]);
  const [availableCombinations, setAvailableCombinations] = useState<any[]>([]);
  const [scatterDateFilter, setScatterDateFilter] = useState<string>('last_month');

  // Check if we're on the no-auth route
  const isNoAuthRoute = window.location.pathname.includes('/no-auth/');

  // Check if aggregated stats is enabled
  const isAggregatedStatsEnabled = window.ENV_CONFIG?.ENABLE_AGGREGATED_STATS || false;

  // Calculate date range based on filter
  const getDateRange = (filter: string): [string, string] => {
    const today = dayjs();
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs = today;

    switch (filter) {
      case 'today':
        startDate = today.startOf('day');
        break;
      case 'yesterday':
        startDate = today.subtract(1, 'day').startOf('day');
        endDate = today.subtract(1, 'day').endOf('day');
        break;
      case 'last_7_days':
        startDate = today.subtract(7, 'days');
        break;
      case 'last_month':
        startDate = today.subtract(30, 'days');
        break;
      case 'last_3_months':
        startDate = today.subtract(90, 'days');
        break;
      case 'all_time':
        startDate = dayjs('2020-01-01');
        break;
      default:
        startDate = today.subtract(30, 'days');
    }

    return [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')];
  };

  // Fetch data when component mounts or date filter changes
  useEffect(() => {
    if (isAggregatedStatsEnabled || isNoAuthRoute) {
      fetchCenterAnalytics();
      fetch7DayTrendData();
    } else {
      setLoading(false);
    }
  }, [isAggregatedStatsEnabled, isNoAuthRoute, dateFilter]);

  // Fetch 7-day trend data when selected center changes
  useEffect(() => {
    if (isAggregatedStatsEnabled || isNoAuthRoute) {
      fetch7DayTrendData();
    }
  }, [selectedTrendCenter]);

  // Fetch scatter plot data when component mounts or filters change
  useEffect(() => {
    if (isAggregatedStatsEnabled || isNoAuthRoute) {
      fetchScatterPlotData();
    }
  }, [scatterDateFilter]);

  // Update selected body parts when modality changes
  useEffect(() => {
    if (availableCombinations.length > 0) {
      const defaultBodyParts = DEFAULT_BODY_PARTS_BY_MODALITY[selectedModality] || [];
      const availableForModality = availableCombinations
        .filter(c => c.modality === selectedModality)
        .map(c => normalizeBodyPart(c.body_part));

      const toSelect = defaultBodyParts.filter(bp => availableForModality.includes(bp)).slice(0, 5);
      setSelectedBodyParts(toSelect);
    }
  }, [selectedModality, availableCombinations]);

  const fetchCenterAnalytics = async () => {
    setLoading(true);
    try {
      // Get date range from filter
      const [start_date, end_date] = getDateRange(dateFilter);

      const payload = { start_date, end_date };

      // Use no-auth endpoint if accessed via no-auth route
      const endpoint = isNoAuthRoute ? '/no-auth/get-center-analytics' : '/get-center-analytics';
      const response = await makePostCall(endpoint, payload);

      if (response?.data?.success) {
        const centerData = response.data.data || [];
        setData(centerData);
        setSummary(response.data.summary || null);

        // Initialize with all centers (dev/demo already excluded by backend)
        const selectedCenters = centerData.map(c => c.center_id);
        setSelectedCenters(selectedCenters);
      } else {
        message.error('Failed to load center analytics');
      }
    } catch (error) {
      console.error('Error fetching center analytics:', error);
      message.error('An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  const fetch7DayTrendData = async () => {
    setTrendLoading(true);
    try {
      const payload = { center_id: selectedTrendCenter };

      // Use no-auth endpoint if accessed via no-auth route
      const endpoint = isNoAuthRoute ? '/no-auth/get-7-day-trend-analytics' : '/get-7-day-trend-analytics';
      const response = await makePostCall(endpoint, payload);

      if (response?.data?.success) {
        setTrendData(response.data.data);
      } else {
        message.error('Failed to load 7-day trend data');
      }
    } catch (error) {
      console.error('Error fetching 7-day trend analytics:', error);
      message.error('An error occurred while loading trend data');
    } finally {
      setTrendLoading(false);
    }
  };

  const fetchScatterPlotData = async () => {
    setScatterLoading(true);
    try {
      const [start_date, end_date] = getDateRange(scatterDateFilter);
      const payload = {
        start_date,
        end_date,
        center_id: 'all_centers'
      };

      const endpoint = isNoAuthRoute ? '/no-auth/get-body-part-scatter-plot-data' : '/get-body-part-scatter-plot-data';
      const response = await makePostCall(endpoint, payload);

      if (response?.data?.success) {
        setScatterData(response.data.data.scatter_data || []);
        setAvailableCombinations(response.data.data.available_combinations || []);

        // Auto-select default body parts for the selected modality
        const defaultBodyParts = DEFAULT_BODY_PARTS_BY_MODALITY[selectedModality] || [];
        const availableForModality = response.data.data.available_combinations
          ?.filter(c => c.modality === selectedModality)
          ?.map(c => normalizeBodyPart(c.body_part)) || [];

        const toSelect = defaultBodyParts.filter(bp => availableForModality.includes(bp)).slice(0, 5);
        setSelectedBodyParts(toSelect);
      } else {
        message.error('Failed to load scatter plot data');
      }
    } catch (error) {
      console.error('Error fetching scatter plot data:', error);
      message.error('An error occurred while loading scatter plot data');
    } finally {
      setScatterLoading(false);
    }
  };

  // Status badge renderer
  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      new: { color: 'blue', icon: <ClockCircleOutlined />, text: 'New' },
      fast: { color: 'green', icon: <CheckCircleOutlined />, text: 'Excellent' },
      medium: { color: 'orange', icon: <WarningOutlined />, text: 'Good' },
      slow: { color: 'red', icon: <WarningOutlined />, text: 'Needs Attention' }
    };

    const config = statusConfig[status] || statusConfig.medium;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Format center name for display
  const formatCenterName = (centerId: string) => {
    // Replace underscores/hyphens with spaces and capitalize words
    return centerId
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Overview Table Columns
  const overviewColumns = [
    {
      title: 'Center Name',
      dataIndex: 'center_id',
      key: 'center_id',
      fixed: 'left' as const,
      width: 250,
      sorter: (a: CenterData, b: CenterData) => (a.center_name || a.center_id).localeCompare(b.center_name || b.center_id),
      sortOrder: overviewSortedInfo.columnKey === 'center_id' ? overviewSortedInfo.order : null,
      render: (text: string, record: CenterData) => (
        <strong style={{ color: '#1890ff' }}>{record.center_name || formatCenterName(text)}</strong>
      )
    },
    {
      title: 'Users',
      dataIndex: 'total_users',
      key: 'total_users',
      width: 100,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => a.total_users - b.total_users,
      sortOrder: overviewSortedInfo.columnKey === 'total_users' ? overviewSortedInfo.order : null,
      render: (value: number) => <Tag color="purple">{value}</Tag>
    },
    {
      title: 'Total Studies',
      dataIndex: 'total_studies_all_modalities',
      key: 'total_studies',
      width: 150,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) =>
        a.total_studies_all_modalities - b.total_studies_all_modalities,
      sortOrder: overviewSortedInfo.columnKey === 'total_studies' ? overviewSortedInfo.order : null,
      render: (value: number, record: CenterData) => {
        const pushed = record.total_pushed || 0;
        return (
          <Tag color="geekblue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
            {pushed ? `${value} / ${pushed}` : value}
          </Tag>
        );
      }
    },
    // {
    //   title: 'Total Views',
    //   dataIndex: 'total_views',
    //   key: 'total_views',
    //   width: 130,
    //   align: 'center' as const,
    //   sorter: (a: CenterData, b: CenterData) => a.total_views - b.total_views,
    //   sortOrder: overviewSortedInfo.columnKey === 'total_views' ? overviewSortedInfo.order : null,
    //   render: (value: number) => (
    //     <div>
    //       <EyeOutlined style={{ marginRight: 6, color: '#52c41a' }} />
    //       <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{value.toLocaleString()}</span>
    //     </div>
    //   )
    // },
    // {
    //   title: 'Active Days',
    //   dataIndex: 'active_days',
    //   key: 'active_days',
    //   width: 130,
    //   align: 'center' as const,
    //   sorter: (a: CenterData, b: CenterData) => a.active_days - b.active_days,
    //   sortOrder: overviewSortedInfo.columnKey === 'active_days' ? overviewSortedInfo.order : null,
    //   render: (value: number) => (
    //     <div>
    //       <CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />
    //       <span style={{ fontSize: '14px' }}>{value}</span>
    //     </div>
    //   )
    // }
  ];

  // Performance Table Columns
  const performanceColumns = [
    {
      title: 'Center Name',
      dataIndex: 'center_id',
      key: 'center_id',
      fixed: 'left' as const,
      width: 250,
      sorter: (a: CenterData, b: CenterData) => (a.center_name || a.center_id).localeCompare(b.center_name || b.center_id),
      sortOrder: performanceSortedInfo.columnKey === 'center_id' ? performanceSortedInfo.order : null,
      render: (text: string, record: CenterData) => (
        <strong style={{ color: '#1890ff' }}>{record.center_name || formatCenterName(text)}</strong>
      )
    },
    // CT Columns
    {
      title: 'CT Studies',
      dataIndex: 'ct_studies',
      key: 'ct_studies',
      width: 140,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.ct_studies || 0) - (b.ct_studies || 0),
      sortOrder: performanceSortedInfo.columnKey === 'ct_studies' ? performanceSortedInfo.order : null,
      render: (value: number | null, record: CenterData) => {
        if (!value && !record.ct_pushed) return <span style={{ color: '#999' }}>—</span>;
        const viewed = value || 0;
        const pushed = record.ct_pushed || 0;
        if (pushed) {
          return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>{viewed} / {pushed}</span>;
        }
        return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>{viewed}</span>;
      }
    },
    {
      title: 'CT Avg Time',
      dataIndex: 'ct_avg_seconds',
      key: 'ct_avg_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.ct_avg_seconds || 0) - (b.ct_avg_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'ct_avg_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#1890ff' }}>{value}s</span>;
      }
    },
    {
      title: 'CT P90 Time',
      dataIndex: 'ct_p90_seconds',
      key: 'ct_p90_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.ct_p90_seconds || 0) - (b.ct_p90_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'ct_p90_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#1890ff' }}>{value}s</span>;
      }
    },
    // MR Columns
    {
      title: 'MR Studies',
      dataIndex: 'mr_studies',
      key: 'mr_studies',
      width: 140,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.mr_studies || 0) - (b.mr_studies || 0),
      sortOrder: performanceSortedInfo.columnKey === 'mr_studies' ? performanceSortedInfo.order : null,
      render: (value: number | null, record: CenterData) => {
        if (!value && !record.mr_pushed) return <span style={{ color: '#999' }}>—</span>;
        const viewed = value || 0;
        const pushed = record.mr_pushed || 0;
        if (pushed) {
          return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>{viewed} / {pushed}</span>;
        }
        return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>{viewed}</span>;
      }
    },
    {
      title: 'MR Avg Time',
      dataIndex: 'mr_avg_seconds',
      key: 'mr_avg_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.mr_avg_seconds || 0) - (b.mr_avg_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'mr_avg_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#52c41a' }}>{value}s</span>;
      }
    },
    {
      title: 'MR P90 Time',
      dataIndex: 'mr_p90_seconds',
      key: 'mr_p90_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.mr_p90_seconds || 0) - (b.mr_p90_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'mr_p90_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#52c41a' }}>{value}s</span>;
      }
    },
    // X-Ray Columns
    {
      title: 'X-Ray Equivalents',
      dataIndex: 'dx_studies',
      key: 'dx_studies',
      width: 140,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.dx_studies || 0) - (b.dx_studies || 0),
      sortOrder: performanceSortedInfo.columnKey === 'dx_studies' ? performanceSortedInfo.order : null,
      render: (value: number | null, record: CenterData) => {
        if (!value && !record.dx_pushed) return <span style={{ color: '#999' }}>—</span>;
        const viewed = Math.ceil((value || 0) / 3);
        const pushed = Math.ceil((record.dx_pushed || 0) / 3);
        if (pushed) {
          return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#fa8c16' }}>{viewed} / {pushed}</span>;
        }
        return <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#fa8c16' }}>{viewed}</span>;
      }
    },
    {
      title: 'XR Avg Time',
      dataIndex: 'dx_avg_seconds',
      key: 'dx_avg_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.dx_avg_seconds || 0) - (b.dx_avg_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'dx_avg_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#fa8c16' }}>{value}s</span>;
      }
    },
    {
      title: 'XR P90 Time',
      dataIndex: 'dx_p90_seconds',
      key: 'dx_p90_seconds',
      width: 120,
      align: 'center' as const,
      sorter: (a: CenterData, b: CenterData) => (a.dx_p90_seconds || 0) - (b.dx_p90_seconds || 0),
      sortOrder: performanceSortedInfo.columnKey === 'dx_p90_seconds' ? performanceSortedInfo.order : null,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#999' }}>—</span>;
        return <span style={{ fontSize: '14px', color: '#fa8c16' }}>{value}s</span>;
      }
    },
    // {
    //   title: 'Performance Status',
    //   dataIndex: 'performance_status',
    //   key: 'performance_status',
    //   width: 180,
    //   align: 'center' as const,
    //   filters: [
    //     { text: 'Excellent', value: 'fast' },
    //     { text: 'Good', value: 'medium' },
    //     { text: 'Needs Attention', value: 'slow' },
    //     { text: 'New', value: 'new' }
    //   ],
    //   onFilter: (value: any, record: CenterData) => record.performance_status === value,
    //   render: renderStatusBadge
    // }
  ];

  const handleOverviewTableChange = (pagination: any, filters: any, sorter: any) => {
    setOverviewSortedInfo(sorter);
  };

  const handlePerformanceTableChange = (pagination: any, filters: any, sorter: any) => {
    setPerformanceSortedInfo(sorter);
  };

  // Prepare modality distribution chart data
  const modalityChartData = useMemo(() => {
    return data
      .filter(center => selectedCenters.includes(center.center_id))
      .map(center => {
        const xrayCount = Math.ceil((center.dx_studies || 0) / 3);
        return {
          name: center.center_name || formatCenterName(center.center_id),
          CT: center.ct_studies || 0,
          MR: center.mr_studies || 0,
          XR: xrayCount,
          total: (center.ct_studies || 0) + (center.mr_studies || 0) + xrayCount
        };
      })
      .sort((a, b) => b.total - a.total); // Sort by total studies descending
  }, [data, selectedCenters]);

  // Prepare load time comparison chart data
  const loadTimeChartData = useMemo(() => {
    return data
      .filter(center => selectedCenters.includes(center.center_id))
      .filter(center => center.ct_avg_seconds || center.mr_avg_seconds || center.dx_avg_seconds) // Only centers with load time data
      .map(center => ({
        name: center.center_name || formatCenterName(center.center_id),
        'CT Avg Load': center.ct_avg_seconds || null,
        'MR Avg Load': center.mr_avg_seconds || null,
        'X-Ray Avg Load': center.dx_avg_seconds || null,
        center_id: center.center_id
      }))
      .sort((a, b) => {
        // Sort by highest average load time
        const aMax = Math.max(a['CT Avg Load'] || 0, a['MR Avg Load'] || 0, a['X-Ray Avg Load'] || 0);
        const bMax = Math.max(b['CT Avg Load'] || 0, b['MR Avg Load'] || 0, b['X-Ray Avg Load'] || 0);
        return bMax - aMax;
      });
  }, [data, selectedCenters]);

  // Custom tooltip for modality chart
  const ModalityTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + entry.value, 0);
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: <strong>{entry.value}</strong> studies
            </p>
          ))}
          <p style={{ margin: '8px 0 0 0', paddingTop: 8, borderTop: '1px solid #f0f0f0', fontWeight: 'bold' }}>
            Total: {total} studies
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for load time chart
  const LoadTimeTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>{label}</p>
          {payload.map((entry, index) => {
            if (entry.value === null) return null;
            return (
              <p key={index} style={{ margin: '4px 0', color: entry.color }}>
                {entry.name}: <strong>{entry.value}s</strong>
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Custom bar color based on load time thresholds and modality-specific standards
  const getLoadTimeBarColor = (value: number, modality: 'CT' | 'MR' | 'XR') => {
    if (!value) return '#d9d9d9';

    // Modality-specific standards: XR ≤ 5s, CT ≤ 20s, MR ≤ 25s
    const standards = {
      'XR': 5,
      'CT': 20,
      'MR': 25
    };

    const standard = standards[modality];

    // Color coding: Green (meets standard), Orange (slightly over), Red (significantly over)
    if (value <= standard) return '#52c41a'; // Green - Meets standard
    if (value <= standard * 1.5) return '#faad14'; // Orange - Moderately over
    return '#ff4d4f'; // Red - Significantly over
  };

  // Color palette for different body parts
  const bodyPartColors = [
    '#1890ff', // Blue
    '#52c41a', // Green
    '#fa8c16', // Orange
    '#722ed1', // Purple
    '#eb2f96', // Magenta
    '#13c2c2', // Cyan
    '#faad14', // Gold
    '#f5222d', // Red
    '#a0d911', // Lime
    '#2f54eb', // Geek Blue
  ];

  // Get color for a specific body part
  const getBodyPartColor = (bodyPart: string): string => {
    const index = BODY_PART_PRIORITY_ORDER.indexOf(bodyPart);
    if (index !== -1 && index < bodyPartColors.length) {
      return bodyPartColors[index];
    }
    // Fallback: use hash of body part name for consistent color
    const hash = bodyPart.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return bodyPartColors[hash % bodyPartColors.length];
  };

  // Prepare scatter plot data grouped by body part
  const scatterPlotDataByBodyPart = useMemo(() => {
    if (!scatterData || !selectedModality) return {};

    const grouped: Record<string, any[]> = {};

    scatterData
      .filter(item => item.modality === selectedModality)
      .forEach(item => {
        const normalizedBodyPart = normalizeBodyPart(item.body_part);
        if (!grouped[normalizedBodyPart]) {
          grouped[normalizedBodyPart] = [];
        }
        grouped[normalizedBodyPart].push({
          x: item.instances,
          y: parseFloat(item.load_time_seconds),
          studyId: item.study_id,
          center: item.center_id,
          cacheHitRate: parseFloat(item.cache_hit_rate) || 0,
          viewCount: item.view_count || 1,
          bodyPart: normalizedBodyPart
        });
      });

    return grouped;
  }, [scatterData, selectedModality]);

  // Prepare combined scatter plot data (all body parts together)
  const combinedScatterData = useMemo(() => {
    if (!scatterData || !selectedModality || selectedBodyParts.length === 0) return [];

    return scatterData
      .filter(item => item.modality === selectedModality)
      .filter(item => {
        const normalizedBodyPart = normalizeBodyPart(item.body_part);
        return selectedBodyParts.includes(normalizedBodyPart);
      })
      .map(item => {
        const normalizedBodyPart = normalizeBodyPart(item.body_part);
        return {
          x: item.instances,
          y: parseFloat(item.load_time_seconds),
          studyId: item.study_id,
          center: item.center_id,
          cacheHitRate: parseFloat(item.cache_hit_rate) || 0,
          viewCount: item.view_count || 1,
          bodyPart: normalizedBodyPart,
          color: getBodyPartColor(normalizedBodyPart)
        };
      });
  }, [scatterData, selectedModality, selectedBodyParts]);

  // Get available body parts for selected modality
  const availableBodyPartsForModality = useMemo(() => {
    const bodyParts = availableCombinations
      .filter(c => c.modality === selectedModality)
      .map(c => ({
        name: normalizeBodyPart(c.body_part),
        count: parseInt(c.study_count),
        original: c.body_part
      }));

    // Group by normalized name and sum counts
    const grouped: Record<string, { name: string; count: number }> = {};
    bodyParts.forEach(bp => {
      if (!grouped[bp.name]) {
        grouped[bp.name] = { name: bp.name, count: 0 };
      }
      grouped[bp.name].count += bp.count;
    });

    // Sort by priority order
    return Object.values(grouped).sort((a, b) => {
      const aIndex = BODY_PART_PRIORITY_ORDER.indexOf(a.name);
      const bIndex = BODY_PART_PRIORITY_ORDER.indexOf(b.name);
      if (aIndex === -1 && bIndex === -1) return b.count - a.count;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [availableCombinations, selectedModality]);

  // Color map for modalities
  const modalityColors = {
    'CT': '#1890ff',
    'MR': '#52c41a',
    'DX': '#fa8c16'
  };

  // Transform 7-day trend data to divide X-Ray counts by 3
  const transformedTrendModalityCount = useMemo(() => {
    if (!trendData?.modality_count) return [];
    return trendData.modality_count.map(item => ({
      ...item,
      DX: item.DX ? Math.ceil(item.DX / 3) : item.DX
    }));
  }, [trendData?.modality_count]);

  // Scatter plot tooltip
  const ScatterTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>Study Details</p>
          {data.bodyPart && (
            <p style={{ margin: '4px 0' }}>
              Body Part: <strong>{data.bodyPart}</strong>
            </p>
          )}
          <p style={{ margin: '4px 0' }}>
            Instances: <strong>{data.x}</strong>
          </p>
          <p style={{ margin: '4px 0' }}>
            Avg Load Time: <strong>{data.y}s</strong>
          </p>
          <p style={{ margin: '4px 0' }}>
            Center: <strong>{formatCenterName(data.center)}</strong>
          </p>
          {data.viewCount > 1 && (
            <p style={{ margin: '4px 0', fontSize: '12px', color: '#666' }}>
              ({data.viewCount} views averaged)
            </p>
          )}
          {data.cacheHitRate > 0 && (
            <p style={{ margin: '4px 0' }}>
              Avg Cache Hit: <strong>{data.cacheHitRate.toFixed(1)}%</strong>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // If aggregated stats is not enabled and not on no-auth route, show a message
  if (!isAggregatedStatsEnabled && !isNoAuthRoute) {
    return (
      <div className="center-analytics-container">
        <div className="page-header">
          <h1 className="page-title">
            <BankOutlined /> Performance Analytics
          </h1>
        </div>
        <Card style={{ textAlign: 'center', padding: '60px 24px' }}>
          <WarningOutlined style={{ fontSize: 64, color: '#faad14', marginBottom: 16 }} />
          <h2 style={{ fontSize: 24, marginBottom: 16 }}>Feature Not Available</h2>
          <p style={{ fontSize: 16, color: '#666', marginBottom: 24 }}>
            Center Analytics is only available on the analytics server.
            <br />
            This feature requires <code>ENABLE_AGGREGATED_STATS</code> to be enabled in env-config.js
          </p>
          <p style={{ fontSize: 14, color: '#999' }}>
            Contact your system administrator for access.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="center-analytics-container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 className="page-title">
              <BankOutlined /> Performance Analytics
            </h1>
          </div>

          {/* Date Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalendarOutlined style={{ fontSize: 16, color: '#1890ff' }} />
            <Select
              value={dateFilter}
              onChange={setDateFilter}
              style={{ width: 180 }}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                // { label: 'Last 7 Days', value: 'last_7_days' },
                // { label: 'Last Month', value: 'last_month' },
                // { label: 'All Time', value: 'all_time' }
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchCenterAnalytics}
              loading={loading}
              title="Refresh data"
            />
          </div>
        </div>
      </div>

      {/* Performance Standards */}
      <Card style={{ marginBottom: 24, background: '#f0f5ff', borderLeft: '4px solid #1890ff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Performance Standards:
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontWeight: 600, color: '#fa8c16' }}>X-Ray:</span>
              <span style={{ marginLeft: 8, color: '#666' }}>≤ 5 seconds</span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1890ff' }}>CT:</span>
              <span style={{ marginLeft: 8, color: '#666' }}>≤ 20 seconds</span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#52c41a' }}>MR:</span>
              <span style={{ marginLeft: 8, color: '#666' }}>≤ 25 seconds</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Total Centers"
                value={summary.total_centers}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Total Studies (Viewed / Pushed)"
                formatter={() => {
                  const viewed = summary.total_ct_studies + summary.total_mr_studies + Math.ceil(summary.total_dx_studies / 3);
                  const pushed = (summary.total_ct_pushed || 0) + (summary.total_mr_pushed || 0) + Math.ceil((summary.total_dx_pushed || 0) / 3);
                  return pushed ? `${viewed} / ${pushed}` : `${viewed}`;
                }}
                prefix={<DatabaseOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          {/*<Col xs={24} sm={12} md={8} lg={4}>*/}
          {/*  <Card>*/}
          {/*    <Statistic*/}
          {/*      title="Total Views"*/}
          {/*      value={summary.total_views}*/}
          {/*      prefix={<EyeOutlined />}*/}
          {/*      valueStyle={{ color: '#722ed1' }}*/}
          {/*    />*/}
          {/*  </Card>*/}
          {/*</Col>*/}
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="CT Studies (Viewed / Pushed)"
                formatter={() => {
                  const pushed = summary.total_ct_pushed || 0;
                  return pushed ? `${summary.total_ct_studies} / ${pushed}` : `${summary.total_ct_studies}`;
                }}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="MR Studies (Viewed / Pushed)"
                formatter={() => {
                  const pushed = summary.total_mr_pushed || 0;
                  return pushed ? `${summary.total_mr_studies} / ${pushed}` : `${summary.total_mr_studies}`;
                }}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="X-Ray Equiv (Viewed / Pushed)"
                formatter={() => {
                  const viewed = Math.ceil(summary.total_dx_studies / 3);
                  const pushed = Math.ceil((summary.total_dx_pushed || 0) / 3);
                  return pushed ? `${viewed} / ${pushed}` : `${viewed}`;
                }}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Overview Table */}
      {/* <Card className="table-card" style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 16, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
          📊 Center Overview
        </h2>
        <Spin spinning={loading}>
          <Table
            columns={overviewColumns}
            dataSource={data}
            rowKey="center_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} centers`,
              position: ['bottomCenter']
            }}
            onChange={handleOverviewTableChange}
            scroll={{ x: 900 }}
            size="middle"
          />
        </Spin>
      </Card> */}

      {/* Performance Table */}
      <Card className="table-card" style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 16, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
          ⚡ Performance Breakdown
        </h2>
        <Spin spinning={loading}>
          <Table
            columns={performanceColumns}
            dataSource={data}
            rowKey="center_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} centers`,
              position: ['bottomCenter']
            }}
            onChange={handlePerformanceTableChange}
            scroll={{ x: 900 }}
            size="middle"
          />
        </Spin>
      </Card>

      {/* 7-Day Trend Analytics */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
            📈 7-Day Performance Trends (Last 7 Days)
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Select Center:</span>
            <Select
              value={selectedTrendCenter}
              onChange={setSelectedTrendCenter}
              style={{ width: 200 }}
              options={[
                { label: 'All Centers', value: 'all_centers' },
                ...data.map(center => ({
                  label: center.center_name || formatCenterName(center.center_id),
                  value: center.center_id
                }))
              ]}
            />
          </div>
        </div>

        <Spin spinning={trendLoading}>
          <Row gutter={16}>
            {/* Modality Study Count Trend */}
            <Col xs={24} lg={12}>
              <Card className="table-card">
                <h3 style={{ marginBottom: 16, fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                  Study Count by Modality
                </h3>
                {transformedTrendModalityCount && transformedTrendModalityCount.length > 0 ? (
                  <div style={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={transformedTrendModalityCount}
                        margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(date) => dayjs(date).format('MMM DD')}
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          label={{ value: 'Number of Studies', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip
                          labelFormatter={(date) => dayjs(date).format('MMM DD, YYYY')}
                          formatter={(value: number, name: string) => [value, `${name} Studies`]}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="CT"
                          stroke="#1890ff"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="CT"
                        />
                        <Line
                          type="monotone"
                          dataKey="MR"
                          stroke="#52c41a"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="MR"
                        />
                        <Line
                          type="monotone"
                          dataKey="DX"
                          stroke="#fa8c16"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="X-Ray"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
                    <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <p style={{ fontSize: 16 }}>No trend data available for the selected center.</p>
                  </div>
                )}
              </Card>
            </Col>

            {/* Load Time P90 Trend */}
            <Col xs={24} lg={12}>
              <Card className="table-card">
                <h3 style={{ marginBottom: 16, fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                  P90 Load Time by Modality
                </h3>
                {trendData?.load_time_p90 && trendData.load_time_p90.length > 0 ? (
                  <div style={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={trendData.load_time_p90}
                        margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(date) => dayjs(date).format('MMM DD')}
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis
                          label={{ value: 'P90 Load Time (seconds)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip
                          labelFormatter={(date) => dayjs(date).format('MMM DD, YYYY')}
                          formatter={(value: number | null, name: string) => {
                            if (value === null) return ['N/A', name];
                            return [`${value}s`, name];
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="CT"
                          stroke="#1890ff"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="CT"
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="MR"
                          stroke="#52c41a"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="MR"
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="DX"
                          stroke="#fa8c16"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name="X-Ray"
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
                    <ClockCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                    <p style={{ fontSize: 16 }}>No load time data available for the selected center.</p>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      </div>

      {/* Modality Distribution Chart */}
      <Card className="table-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
            <BarChartOutlined /> Modality Distribution by Center
          </h2>

          {/* Center Selection Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Select Centers:</span>
            <Select
              mode="multiple"
              value={selectedCenters}
              onChange={setSelectedCenters}
              placeholder="Choose centers to display"
              style={{ minWidth: 300, maxWidth: 500 }}
              maxTagCount="responsive"
              options={data.map(center => ({
                label: center.center_name || formatCenterName(center.center_id),
                value: center.center_id
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </div>

        {/* Chart */}
        {modalityChartData.length > 0 ? (
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart
                data={modalityChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  label={{ value: 'Number of Studies', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<ModalityTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar
                  dataKey="CT"
                  stackId="a"
                  fill="#1890ff"
                  name="CT Studies"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="MR"
                  stackId="a"
                  fill="#52c41a"
                  name="MR Studies"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="XR"
                  stackId="a"
                  fill="#fa8c16"
                  name="X-Ray Equivalents"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
            <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <p style={{ fontSize: 16 }}>No centers selected. Please select at least one center to view the chart.</p>
          </div>
        )}
      </Card>

      {/* Average Load Time Comparison Chart */}
      <Card className="table-card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626' }}>
            <ClockCircleOutlined /> Average Load Time Comparison
          </h2>

          {/* Center Selection Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Select Centers:</span>
            <Select
              mode="multiple"
              value={selectedCenters}
              onChange={setSelectedCenters}
              placeholder="Choose centers to display"
              style={{ minWidth: 300, maxWidth: 500 }}
              maxTagCount="responsive"
              options={data.map(center => ({
                label: center.center_name || formatCenterName(center.center_id),
                value: center.center_id
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </div>

        <div style={{ marginBottom: 16, padding: '12px', background: '#e6f7ff', borderRadius: '4px', borderLeft: '4px solid #1890ff' }}>
          <div style={{ fontSize: '14px', color: '#000' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Performance Indicators (based on modality standards):</strong>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <span style={{ color: '#52c41a' }}>🟢 Meets Standard (XR ≤ 5s, CT ≤ 20s, MR ≤ 25s)</span>
              <span style={{ color: '#faad14' }}>🟡 Moderately Over (up to 1.5× standard)</span>
              <span style={{ color: '#ff4d4f' }}>🔴 Significantly Over (&gt; 1.5× standard)</span>
            </div>
          </div>
        </div>

        {/* Chart */}
        {loadTimeChartData.length > 0 ? (
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart
                data={loadTimeChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  label={{ value: 'Average Load Time (seconds)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<LoadTimeTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar
                  dataKey="CT Avg Load"
                  fill="#1890ff"
                  name="CT Avg Load Time"
                  radius={[4, 4, 0, 0]}
                >
                  {loadTimeChartData.map((entry, index) => (
                    <Cell
                      key={`ct-${index}`}
                      fill={entry['CT Avg Load'] ? getLoadTimeBarColor(entry['CT Avg Load'], 'CT') : '#d9d9d9'}
                    />
                  ))}
                </Bar>
                <Bar
                  dataKey="MR Avg Load"
                  fill="#52c41a"
                  name="MR Avg Load Time"
                  radius={[4, 4, 0, 0]}
                >
                  {loadTimeChartData.map((entry, index) => (
                    <Cell
                      key={`mr-${index}`}
                      fill={entry['MR Avg Load'] ? getLoadTimeBarColor(entry['MR Avg Load'], 'MR') : '#d9d9d9'}
                    />
                  ))}
                </Bar>
                <Bar
                  dataKey="X-Ray Avg Load"
                  fill="#fa8c16"
                  name="X-Ray Avg Load Time"
                  radius={[4, 4, 0, 0]}
                >
                  {loadTimeChartData.map((entry, index) => (
                    <Cell
                      key={`xr-${index}`}
                      fill={entry['X-Ray Avg Load'] ? getLoadTimeBarColor(entry['X-Ray Avg Load'], 'XR') : '#d9d9d9'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
            <ClockCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <p style={{ fontSize: 16 }}>No centers selected or no load time data available.</p>
          </div>
        )}
      </Card>

      {/* Study Load Time Scatter Plots by Modality and Body Part */}
      <Card className="table-card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#262626', marginBottom: 8 }}>
              📊 Study Load Time Analysis by Body Part
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#666', marginBottom: 8 }}>
              Scatter plots showing <strong>average study load times</strong> vs. number of instances for different modalities and body parts.
              The green dashed line represents the <strong>service line</strong>.
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
              These plots are made from studies which has body part data available.
            </p>
          </div>

          {/* Date Range Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
            <CalendarOutlined style={{ fontSize: 16, color: '#1890ff' }} />
            <Select
              value={scatterDateFilter}
              onChange={setScatterDateFilter}
              style={{ width: 180 }}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Last 7 Days', value: 'last_7_days' },
                { label: 'Last Month', value: 'last_month' },
                { label: 'Last 3 Months', value: 'last_3_months' },
                { label: 'All Time', value: 'all_time' }
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchScatterPlotData}
              loading={scatterLoading}
              title="Refresh scatter plot data"
            />
          </div>
        </div>

        <Spin spinning={scatterLoading}>
          {/* Modality Tabs */}
          <Tabs
            activeKey={selectedModality}
            onChange={setSelectedModality}
            items={[
              {
                key: 'CT',
                label: (
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    <span style={{ color: '#1890ff', marginRight: 6 }}>●</span>
                    CT Scans
                  </span>
                )
              },
              {
                key: 'MR',
                label: (
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    <span style={{ color: '#52c41a', marginRight: 6 }}>●</span>
                    MRI Scans
                  </span>
                )
              },
              {
                key: 'DX',
                label: (
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    <span style={{ color: '#fa8c16', marginRight: 6 }}>●</span>
                    X-Ray / DX
                  </span>
                )
              }
            ]}
          />

          {/* Body Part Selection */}
          <div style={{ marginTop: 16, marginBottom: 24, padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
            <div style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>
              Select Body Parts to Display:
            </div>
            <Checkbox.Group
              value={selectedBodyParts}
              onChange={setSelectedBodyParts}
              style={{ width: '100%' }}
            >
              <Row gutter={[16, 12]}>
                {availableBodyPartsForModality.map(bp => (
                  <Col xs={12} sm={8} md={6} key={bp.name}>
                    <Checkbox value={bp.name} style={{ fontSize: '13px' }}>
                      <span style={{ fontWeight: 500 }}>{bp.name}</span>
                      <Tag color="blue" style={{ marginLeft: 6, fontSize: '11px' }}>
                        {bp.count}
                      </Tag>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </div>

          {/* Scatter Plots Grid */}
          {selectedBodyParts.length > 0 ? (
            <Row gutter={[16, 16]}>
              {/* Combined Chart - All Body Parts Together */}
              {combinedScatterData.length > 0 && (
                <Col xs={24} key="combined">
                  <Card
                    size="small"
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>
                          {selectedModality} - All Selected Body Parts Combined
                        </span>
                        <Tag color="blue">{combinedScatterData.length} studies</Tag>
                      </div>
                    }
                    style={{ height: '100%' }}
                  >
                    <ResponsiveContainer width="100%" height={450}>
                      <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          dataKey="x"
                          name="Instances"
                          label={{
                            value: 'Number of Instances',
                            position: 'insideBottom',
                            offset: -10,
                            style: { fontSize: 12 }
                          }}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          name="Load Time"
                          label={{
                            value: 'Load Time (seconds)',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fontSize: 12 }
                          }}
                        />
                        <Tooltip content={<ScatterTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={50}
                          wrapperStyle={{ fontSize: '12px' }}
                        />

                        {/* Scatter for each body part with unique color */}
                        {selectedBodyParts.map(bodyPart => {
                          const bodyPartData = combinedScatterData.filter(d => d.bodyPart === bodyPart);
                          if (bodyPartData.length === 0) return null;

                          return (
                            <Scatter
                              key={bodyPart}
                              name={bodyPart}
                              data={bodyPartData}
                              fill={getBodyPartColor(bodyPart)}
                              fillOpacity={0.6}
                            />
                          );
                        })}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>
              )}

              {/* Individual Charts Per Body Part */}
              {selectedBodyParts.map(bodyPart => {
                const data = scatterPlotDataByBodyPart[bodyPart] || [];
                if (data.length === 0) return null;

                // Get service line slope (90th percentile performance)
                const slope = getServiceLineSlope(selectedModality, bodyPart);
                const maxInstances = Math.max(...data.map(d => d.x), 100);
                const maxY = Math.max(...data.map(d => d.y), 10);

                // Calculate service line end point
                // Use a reasonable endpoint that stays visible
                const serviceLineEndX = maxInstances * 1.1; // Extend 10% beyond data
                const serviceLineEndY = slope ? slope * serviceLineEndX : null;

                return (
                  <Col xs={24} lg={12} key={bodyPart}>
                    <Card
                      size="small"
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '15px', fontWeight: 600 }}>
                            {selectedModality} - {bodyPart}
                          </span>
                          <Tag color="blue">{data.length} studies</Tag>
                        </div>
                      }
                      style={{ height: '100%' }}
                    >
                      <ResponsiveContainer width="100%" height={350}>
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 50 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            type="number"
                            dataKey="x"
                            name="Instances"
                            label={{
                              value: 'Number of Instances',
                              position: 'insideBottom',
                              offset: -10,
                              style: { fontSize: 12 }
                            }}
                          />
                          <YAxis
                            type="number"
                            dataKey="y"
                            name="Load Time"
                            label={{
                              value: 'Load Time (seconds)',
                              angle: -90,
                              position: 'insideLeft',
                              style: { fontSize: 12 }
                            }}
                          />
                          <Tooltip content={<ScatterTooltip />} />
                          <Legend
                            verticalAlign="top"
                            height={36}
                            wrapperStyle={{ fontSize: '12px' }}
                          />

                          {/* Service line (90th percentile performance) */}
                          {slope && serviceLineEndY && serviceLineEndY > 0 && (
                            <>
                              <ReferenceLine
                                segment={[
                                  { x: 0, y: 0 },
                                  { x: serviceLineEndX, y: serviceLineEndY }
                                ]}
                                stroke="#52c41a"
                                strokeWidth={3}
                                strokeDasharray="8 4"
                                ifOverflow="extendDomain"
                              />
                              <ReferenceLine
                                y={0}
                                stroke="transparent"
                                // label={{
                                //   value: `90th %ile: ${slope.toFixed(4)}s/instance`,
                                //   position: 'insideTopLeft',
                                //   fill: '#52c41a',
                                //   fontSize: 10,
                                //   offset: 10
                                // }}
                              />
                            </>
                          )}

                          <Scatter
                            name={`${selectedModality} ${bodyPart}`}
                            data={data}
                            fill={getBodyPartColor(bodyPart)}
                            fillOpacity={0.6}
                          />
                        </ScatterChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
              <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <p style={{ fontSize: 16 }}>
                Please select at least one body part to view scatter plots.
              </p>
            </div>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default CenterAnalytics;
